import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Coins, Plus, TrendingUp } from 'lucide-react';
import { StewieCreditsPopup } from './StewieCreditsPopup';
import { useCredits } from '@/hooks/useCredits';
import { formatNumberWithCommas } from './utils/creditUtils';

interface StewieCreditsButtonProps {
  variant?: 'default' | 'compact' | 'enhanced';
  showBalance?: boolean;
  showAUM?: boolean;
}

export const StewieCreditsButton = ({ 
  variant = 'enhanced', 
  showBalance = true, 
  showAUM = true 
}: StewieCreditsButtonProps) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { balance: credits, isLoading } = useCredits();

  // Compact version (original style for compatibility)
  if (variant === 'compact') {
    return (
      <>
        <Button
          variant="outline"
          className="flex items-center gap-1 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 rounded-[4px]"
          onClick={() => setIsPopupOpen(true)}
        >
          <Coins size={16} className="text-yellow-500" />
          <span className="hidden md:inline">Stewie Credits</span>
          <span className="md:hidden">Credits</span>
        </Button>
        
        <StewieCreditsPopup 
          isOpen={isPopupOpen} 
          onClose={() => setIsPopupOpen(false)} 
        />
      </>
    );
  }

  // Enhanced version with balance display and better UX
  if (variant === 'enhanced') {
    return (
      <>
        <div className="flex flex-col gap-2 w-full">
          {/* Main Credits Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 transition-all duration-200 hover:shadow-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full">
                  <Coins className="w-4 h-4 text-yellow-600" />
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Stewie Credits
                  </div>
                  {isLoading ? (
                    <div className="h-5 w-16 bg-gray-200 animate-pulse rounded"></div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-gray-900">
                        {formatNumberWithCommas(credits || 0)}
                      </span>
                      {credits > 0 && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                          Active
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <Button
                size="sm"
                onClick={() => setIsPopupOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md transition-all duration-200 transform hover:-translate-y-0.5"
              >
                <Plus className="w-3 h-3 mr-1" />
                <span className="text-xs">Add</span>
              </Button>
            </div>
            
            {/* AUM Display */}
            {showAUM && !isLoading && (
              <div className="mt-2 pt-2 border-t border-blue-100">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1 text-gray-600">
                    <TrendingUp className="w-3 h-3" />
                    <span>Tradeable AUM</span>
                  </div>
                  <span className="font-semibold text-green-700">
                    ${formatNumberWithCommas((credits || 0) * 1000)}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {/* Quick Info */}
          <div className="text-xs text-gray-500 text-center">
            1 credit = $1,000 AUM • 0.1% fee per trade
          </div>
        </div>
        
        <StewieCreditsPopup 
          isOpen={isPopupOpen} 
          onClose={() => setIsPopupOpen(false)} 
        />
      </>
    );
  }

  // Default version (balance-aware but simpler)
  return (
    <>
      <Button
        variant="outline"
        className="flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 rounded-[4px] bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200 hover:border-yellow-300"
        onClick={() => setIsPopupOpen(true)}
      >
        <Coins size={16} className="text-yellow-500" />
        <div className="flex flex-col items-start">
          <span className="text-xs font-medium">Stewie Credits</span>
          {showBalance && !isLoading && (
            <span className="text-xs text-gray-600">
              {formatNumberWithCommas(credits || 0)} available
            </span>
          )}
        </div>
        {isLoading && (
          <div className="w-4 h-4 bg-gray-200 animate-pulse rounded"></div>
        )}
      </Button>
      
      <StewieCreditsPopup 
        isOpen={isPopupOpen} 
        onClose={() => setIsPopupOpen(false)} 
      />
    </>
  );
};
